// Per-dialect synthesizer for CREATE / ALTER / DROP USER (v2 Slice F1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice F1.
//
// USER is db-level on mssql (mapped from a server LOGIN), a top-level
// principal on mysql + oracle, and an alias for ROLE on pgsql (rejected here
// with a redirect to op:'create-role'). sqlite has no auth model.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { UserChange, UserDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { quoteMysqlPrincipal, quoteMysqlString } from '../../../../core/utils/mysqlSqlLiteral';

const PGSQL_HINT
  = `pgsql collapses users into roles. Use op:'create-role' with `
    + `pgsqlLogin: true (and optional pgsqlPassword) for the same shape.`;
const SQLITE_HINT = `sqlite has no auth model. Connection-level access is OS / file-permission driven.`;

function rejectUnsupported(engine: SqlEngine, op: string): void {
  if (engine === 'postgres') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support ${op} — ${PGSQL_HINT}`,
    );
  }
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support ${op} — ${SQLITE_HINT}`,
    );
  }
}

function rejectField(engine: SqlEngine, op: string, field: string, allowed: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} ${op} does not accept '${field}' — only ${allowed} accept this field.`,
  );
}

// ─── CREATE USER ──────────────────────────────────────────────────────────

export function synthesizeCreateUser(engine: SqlEngine, user: UserDef, mysqlNoBackslashEscapes = false): string {
  rejectUnsupported(engine, 'create-user');
  if (engine === 'mssql') return synthMssqlCreateUser(user);
  if (engine === 'mysql') return synthMysqlCreateUser(user, mysqlNoBackslashEscapes);
  return synthOracleCreateUser(user);
}

function synthMssqlCreateUser(user: UserDef): string {
  const mysqlFields = user.mysqlHost ?? user.mysqlIdentifiedBy ?? user.mysqlIdentifiedWith
    ?? user.mysqlRequireText ?? user.mysqlPasswordExpireText;
  if (mysqlFields !== undefined) rejectField('mssql', 'create-user', 'mysql*', 'mysql');
  const oracleFields = user.oracleIdentifiedBy ?? user.oracleDefaultTablespace
    ?? user.oracleTemporaryTablespace ?? user.oracleQuotaText ?? user.oracleProfile;
  if (oracleFields !== undefined || user.oracleIdentifiedExternally !== undefined
      || user.oracleAccountLocked !== undefined) {
    rejectField('mssql', 'create-user', 'oracle*', 'oracle');
  }
  if (user.mssqlForLogin !== undefined && user.mssqlWithoutLogin) {
    throw new Error(
      `applyDdlEdit: mssql create-user cannot set both 'mssqlForLogin' and `
        + `'mssqlWithoutLogin' — they are mutually exclusive principal kinds.`,
    );
  }
  const name = maybeQuoteIdent('mssql', user.name, user.quoted ?? false);
  let s = `CREATE USER ${name}`;
  if (user.mssqlForLogin !== undefined) s += ` FOR LOGIN ${user.mssqlForLogin}`;
  else if (user.mssqlWithoutLogin) s += ' WITHOUT LOGIN';
  if (user.mssqlDefaultSchema !== undefined) {
    s += ` WITH DEFAULT_SCHEMA = ${user.mssqlDefaultSchema}`;
  }
  return s;
}

function synthMysqlCreateUser(user: UserDef, mysqlNoBackslashEscapes: boolean): string {
  if (user.mssqlForLogin !== undefined || user.mssqlWithoutLogin !== undefined
      || user.mssqlDefaultSchema !== undefined) {
    rejectField('mysql', 'create-user', 'mssql*', 'mssql');
  }
  if (user.oracleIdentifiedBy !== undefined || user.oracleIdentifiedExternally !== undefined
      || user.oracleDefaultTablespace !== undefined || user.oracleTemporaryTablespace !== undefined
      || user.oracleQuotaText !== undefined || user.oracleProfile !== undefined
      || user.oracleAccountLocked !== undefined) {
    rejectField('mysql', 'create-user', 'oracle*', 'oracle');
  }
  if (user.mysqlIdentifiedBy !== undefined && user.mysqlIdentifiedWith !== undefined) {
    throw new Error(
      `applyDdlEdit: mysql create-user cannot set both 'mysqlIdentifiedBy' `
        + `(SQL auth password) and 'mysqlIdentifiedWith' (auth plugin).`,
    );
  }
  let s = `CREATE USER ${quoteMysqlPrincipal(user.name, user.mysqlHost)}`;
  if (user.mysqlIdentifiedBy !== undefined) s += ` IDENTIFIED BY ${quoteMysqlString(user.mysqlIdentifiedBy, mysqlNoBackslashEscapes)}`;
  else if (user.mysqlIdentifiedWith !== undefined) s += ` IDENTIFIED WITH ${user.mysqlIdentifiedWith}`;
  if (user.mysqlRequireText !== undefined) s += ` REQUIRE ${user.mysqlRequireText}`;
  if (user.mysqlPasswordExpireText !== undefined) {
    s += ` PASSWORD EXPIRE ${user.mysqlPasswordExpireText}`;
  }
  return s;
}

function synthOracleCreateUser(user: UserDef): string {
  if (user.mssqlForLogin !== undefined || user.mssqlWithoutLogin !== undefined
      || user.mssqlDefaultSchema !== undefined) {
    rejectField('oracle', 'create-user', 'mssql*', 'mssql');
  }
  if (user.mysqlHost !== undefined || user.mysqlIdentifiedBy !== undefined
      || user.mysqlIdentifiedWith !== undefined || user.mysqlRequireText !== undefined
      || user.mysqlPasswordExpireText !== undefined) {
    rejectField('oracle', 'create-user', 'mysql*', 'mysql');
  }
  if (user.oracleIdentifiedBy !== undefined && user.oracleIdentifiedExternally) {
    throw new Error(
      `applyDdlEdit: oracle create-user cannot set both 'oracleIdentifiedBy' `
        + `and 'oracleIdentifiedExternally' — they are mutually exclusive auth kinds.`,
    );
  }
  if (user.oracleIdentifiedBy === undefined && !user.oracleIdentifiedExternally) {
    throw new Error(
      `applyDdlEdit: oracle create-user requires one of 'oracleIdentifiedBy' `
        + `(SQL auth) or 'oracleIdentifiedExternally: true' (OS auth).`,
    );
  }
  const name = maybeQuoteIdent('oracle', user.name, user.quoted ?? false);
  let s = `CREATE USER ${name}`;
  if (user.oracleIdentifiedBy !== undefined) s += ` IDENTIFIED BY ${user.oracleIdentifiedBy}`;
  else s += ' IDENTIFIED EXTERNALLY';
  if (user.oracleDefaultTablespace !== undefined) {
    s += ` DEFAULT TABLESPACE ${user.oracleDefaultTablespace}`;
  }
  if (user.oracleTemporaryTablespace !== undefined) {
    s += ` TEMPORARY TABLESPACE ${user.oracleTemporaryTablespace}`;
  }
  if (user.oracleQuotaText !== undefined) s += ` QUOTA ${user.oracleQuotaText}`;
  if (user.oracleProfile !== undefined) s += ` PROFILE ${user.oracleProfile}`;
  if (user.oracleAccountLocked !== undefined) {
    s += user.oracleAccountLocked ? ' ACCOUNT LOCK' : ' ACCOUNT UNLOCK';
  }
  return s;
}

// ─── ALTER USER ───────────────────────────────────────────────────────────

export function synthesizeAlterUser(
  engine: SqlEngine,
  userName: string,
  quoted: boolean,
  mysqlHost: string | undefined,
  change: UserChange,
  mysqlNoBackslashEscapes = false,
): string {
  rejectUnsupported(engine, 'alter-user');
  const { mssqlDefaultSchema, mssqlName, mysqlIdentifiedBy, mysqlPasswordExpireText,
    oracleIdentifiedBy, oracleAccountLocked } = change;
  if (mssqlDefaultSchema === undefined && mssqlName === undefined
      && mysqlIdentifiedBy === undefined && mysqlPasswordExpireText === undefined
      && oracleIdentifiedBy === undefined && oracleAccountLocked === undefined) {
    throw new Error(`applyDdlEdit: alter-user requires at least one change field.`);
  }
  if (engine === 'mssql') {
    if (mysqlIdentifiedBy !== undefined || mysqlPasswordExpireText !== undefined) {
      rejectField('mssql', 'alter-user', 'mysql*', 'mysql');
    }
    if (oracleIdentifiedBy !== undefined || oracleAccountLocked !== undefined) {
      rejectField('mssql', 'alter-user', 'oracle*', 'oracle');
    }
    const name = maybeQuoteIdent('mssql', userName, quoted);
    const parts: string[] = [];
    if (mssqlName !== undefined) parts.push(`NAME = ${mssqlName}`);
    if (mssqlDefaultSchema !== undefined) parts.push(`DEFAULT_SCHEMA = ${mssqlDefaultSchema}`);
    return `ALTER USER ${name} WITH ${parts.join(', ')}`;
  }
  if (engine === 'mysql') {
    if (mssqlDefaultSchema !== undefined || mssqlName !== undefined) {
      rejectField('mysql', 'alter-user', 'mssql*', 'mssql');
    }
    if (oracleIdentifiedBy !== undefined || oracleAccountLocked !== undefined) {
      rejectField('mysql', 'alter-user', 'oracle*', 'oracle');
    }
    let s = `ALTER USER ${quoteMysqlPrincipal(userName, mysqlHost)}`;
    if (mysqlIdentifiedBy !== undefined) s += ` IDENTIFIED BY ${quoteMysqlString(mysqlIdentifiedBy, mysqlNoBackslashEscapes)}`;
    if (mysqlPasswordExpireText !== undefined) s += ` PASSWORD EXPIRE ${mysqlPasswordExpireText}`;
    return s;
  }
  // oracle
  if (mssqlDefaultSchema !== undefined || mssqlName !== undefined) {
    rejectField('oracle', 'alter-user', 'mssql*', 'mssql');
  }
  if (mysqlIdentifiedBy !== undefined || mysqlPasswordExpireText !== undefined) {
    rejectField('oracle', 'alter-user', 'mysql*', 'mysql');
  }
  const name = maybeQuoteIdent('oracle', userName, quoted);
  let s = `ALTER USER ${name}`;
  if (oracleIdentifiedBy !== undefined) s += ` IDENTIFIED BY ${oracleIdentifiedBy}`;
  if (oracleAccountLocked !== undefined) {
    s += oracleAccountLocked ? ' ACCOUNT LOCK' : ' ACCOUNT UNLOCK';
  }
  return s;
}

// ─── DROP USER ────────────────────────────────────────────────────────────

export function synthesizeDropUser(
  engine: SqlEngine,
  userName: string,
  quoted: boolean,
  ifExists: boolean,
  mysqlHost: string | undefined,
): string {
  rejectUnsupported(engine, 'drop-user');
  if (engine === 'mysql') {
    let s = 'DROP USER';
    if (ifExists) s += ' IF EXISTS';
    s += ` ${quoteMysqlPrincipal(userName, mysqlHost)}`;
    return s;
  }
  // Oracle's DROP USER has no IF EXISTS clause (mirrors drop-sequence).
  if (ifExists && engine === 'oracle') {
    rejectField('oracle', 'drop-user', 'ifExists', 'mssql / mysql');
  }
  const name = maybeQuoteIdent(engine, userName, quoted);
  let s = 'DROP USER';
  if (ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  return s;
}
